// Motion Animations Skill
// Injected at src/skills/motion-animations.jsx — DO NOT modify in generated apps.
// Requires: motion (available via @mondaydotcomorg/vibe-skills dependencies)

import React, { Children, useMemo } from 'react';
import {
  motion,
  AnimatePresence,
  useReducedMotion,
} from 'motion/react';

export { AnimatePresence };

// ─── Spring presets ──────────────────────────────────────────────────────────

export const SPRING = {
  gentle: { type: 'spring', stiffness: 300, damping: 20, mass: 1 },
  snappy: { type: 'spring', stiffness: 500, damping: 25, mass: 0.8 },
  bouncy: { type: 'spring', stiffness: 400, damping: 12, mass: 1 },
  slow: { type: 'spring', stiffness: 100, damping: 30, mass: 2 },
};

// ─── Duration presets (seconds) ──────────────────────────────────────────────

export const DURATION = {
  fast: 0.15,
  normal: 0.3,
  slow: 0.5,
  entrance: 0.6,
};

// ─── Easing presets ──────────────────────────────────────────────────────────

export const EASING = {
  easeOut: [0.22, 1, 0.36, 1],
  easeInOut: [0.4, 0, 0.2, 1],
  overshoot: [0.34, 1.56, 0.64, 1],
};

// ─── Internal helpers ────────────────────────────────────────────────────────

function useSkipAnimation() {
  return useReducedMotion();
}

const DIRECTION_OFFSETS = {
  up: { x: 0, y: 1 },
  down: { x: 0, y: -1 },
  left: { x: 1, y: 0 },
  right: { x: -1, y: 0 },
};

// ─── FadeIn ──────────────────────────────────────────────────────────────────
// Fades content in with an optional directional slide.

export function FadeIn({
  children,
  direction = null,
  distance = 20,
  delay = 0,
  duration = DURATION.entrance,
  spring = null,
  once = true,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  const offset = direction ? DIRECTION_OFFSETS[direction] : null;

  const initial = skip
    ? undefined
    : {
        opacity: 0,
        ...(offset && { x: offset.x * distance, y: offset.y * distance }),
      };

  const animate = { opacity: 1, x: 0, y: 0 };

  const transition = spring || {
    duration,
    delay,
    ease: EASING.easeOut,
  };

  return (
    <motion.div
      initial={initial}
      whileInView={once ? animate : undefined}
      animate={once ? undefined : animate}
      viewport={once ? { once: true, amount: 0.1 } : undefined}
      transition={skip ? { duration: 0 } : transition}
      style={style}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── SlideIn ─────────────────────────────────────────────────────────────────
// Slides content in from a specified direction.

export function SlideIn({
  children,
  from = 'left',
  distance = 40,
  delay = 0,
  duration = DURATION.entrance,
  spring = null,
  once = true,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();
  const offset = DIRECTION_OFFSETS[from] || DIRECTION_OFFSETS.left;

  const initial = skip
    ? undefined
    : { opacity: 0, x: offset.x * distance, y: offset.y * distance };

  const animate = { opacity: 1, x: 0, y: 0 };

  const transition = spring || {
    duration,
    delay,
    ease: EASING.easeOut,
  };

  return (
    <motion.div
      initial={initial}
      whileInView={once ? animate : undefined}
      animate={once ? undefined : animate}
      viewport={once ? { once: true, amount: 0.1 } : undefined}
      transition={skip ? { duration: 0 } : transition}
      style={style}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── ScaleIn ─────────────────────────────────────────────────────────────────
// Scales content in from a smaller size.

export function ScaleIn({
  children,
  from = 0.85,
  delay = 0,
  duration = DURATION.entrance,
  spring = null,
  once = true,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  const initial = skip ? undefined : { opacity: 0, scale: from };
  const animate = { opacity: 1, scale: 1 };

  const transition = spring || SPRING.gentle;
  const finalTransition = skip
    ? { duration: 0 }
    : { ...transition, delay };

  return (
    <motion.div
      initial={initial}
      whileInView={once ? animate : undefined}
      animate={once ? undefined : animate}
      viewport={once ? { once: true, amount: 0.1 } : undefined}
      transition={finalTransition}
      style={style}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── ScrollReveal ────────────────────────────────────────────────────────────
// Triggers an animation when the element scrolls into the viewport.

export function ScrollReveal({
  children,
  animation = 'fadeUp',
  distance = 30,
  duration = DURATION.entrance,
  delay = 0,
  threshold = 0.15,
  once = true,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  const animations = {
    fadeUp: { opacity: 0, y: distance },
    fadeDown: { opacity: 0, y: -distance },
    fadeLeft: { opacity: 0, x: distance },
    fadeRight: { opacity: 0, x: -distance },
    scaleUp: { opacity: 0, scale: 0.9 },
    fade: { opacity: 0 },
  };

  const initial = skip ? undefined : (animations[animation] || animations.fadeUp);
  const animate = { opacity: 1, x: 0, y: 0, scale: 1 };

  return (
    <motion.div
      initial={initial}
      whileInView={animate}
      viewport={{ once, amount: threshold }}
      transition={
        skip
          ? { duration: 0 }
          : { duration, delay, ease: EASING.easeOut }
      }
      style={style}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── AnimatedList ────────────────────────────────────────────────────────────
// Staggers children entrances for list-like layouts.

export function AnimatedList({
  children,
  stagger = 0.06,
  animation = 'fadeUp',
  distance = 20,
  duration = DURATION.normal,
  once = true,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  const animations = {
    fadeUp: { opacity: 0, y: distance },
    fadeDown: { opacity: 0, y: -distance },
    fadeLeft: { opacity: 0, x: distance },
    fadeRight: { opacity: 0, x: -distance },
    scaleUp: { opacity: 0, scale: 0.9 },
    fade: { opacity: 0 },
  };

  const initial = animations[animation] || animations.fadeUp;
  const animate = { opacity: 1, x: 0, y: 0, scale: 1 };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: skip ? 0 : stagger,
      },
    },
  };

  const itemVariants = {
    hidden: skip ? {} : initial,
    visible: {
      ...animate,
      transition: skip
        ? { duration: 0 }
        : { duration, ease: EASING.easeOut },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView={once ? 'visible' : undefined}
      animate={once ? undefined : 'visible'}
      viewport={once ? { once: true, amount: 0.05 } : undefined}
      style={style}
      className={className}
      {...props}
    >
      {Children.map(children, (child) =>
        child ? (
          <motion.div variants={itemVariants}>{child}</motion.div>
        ) : null,
      )}
    </motion.div>
  );
}

// ─── HoverGrow ───────────────────────────────────────────────────────────────
// Scales up and optionally lifts the element on hover.

export function HoverGrow({
  children,
  scale = 1.04,
  lift = 4,
  spring = SPRING.snappy,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  return (
    <motion.div
      whileHover={
        skip ? undefined : { scale, y: -lift }
      }
      whileTap={skip ? undefined : { scale: scale * 0.97 }}
      transition={skip ? { duration: 0 } : spring}
      style={{ cursor: 'pointer', ...style }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── PressScale ──────────────────────────────────────────────────────────────
// Shrinks the element when pressed/tapped for tactile feedback.

export function PressScale({
  children,
  scale = 0.96,
  hoverScale = 1.02,
  spring = SPRING.snappy,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  return (
    <motion.div
      whileHover={skip ? undefined : { scale: hoverScale }}
      whileTap={skip ? undefined : { scale }}
      transition={skip ? { duration: 0 } : spring}
      style={{ cursor: 'pointer', ...style }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}

// ─── Reveal ──────────────────────────────────────────────────────────────────
// Generic entrance animation wrapper with configurable type.

export function Reveal({
  children,
  type = 'fade',
  direction = 'up',
  distance = 20,
  scale: fromScale = 0.85,
  delay = 0,
  duration = DURATION.entrance,
  spring = null,
  once = true,
  style,
  className,
  ...props
}) {
  const skip = useSkipAnimation();

  const offset = DIRECTION_OFFSETS[direction] || DIRECTION_OFFSETS.up;

  const initialMap = {
    fade: { opacity: 0 },
    slide: { opacity: 0, x: offset.x * distance, y: offset.y * distance },
    scale: { opacity: 0, scale: fromScale },
    slideScale: {
      opacity: 0,
      x: offset.x * distance,
      y: offset.y * distance,
      scale: fromScale,
    },
  };

  const initial = skip ? undefined : (initialMap[type] || initialMap.fade);
  const animate = { opacity: 1, x: 0, y: 0, scale: 1 };

  const transition = spring || { duration, delay, ease: EASING.easeOut };

  return (
    <motion.div
      initial={initial}
      whileInView={once ? animate : undefined}
      animate={once ? undefined : animate}
      viewport={once ? { once: true, amount: 0.1 } : undefined}
      transition={skip ? { duration: 0 } : transition}
      style={style}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
